import React, { useMemo, useState, useContext, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Tooltip,
  Fab,
  CircularProgress,
  Portal,
  Slide,
  Fade,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  keyframes,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FeedbackIcon from "@mui/icons-material/Feedback";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import { BodycamContext } from "../../context/BodycamContext";
import { useLayout } from "../../context/LayoutContext";
import { rmsClient } from "../../api/clients";

type Option = { value: string; label: string; icon?: React.ReactNode };

interface ReportIssueWidgetProps {
  postUrl?: string;
  options?: Option[];
}

const MAX_MESSAGE_LENGTH = 1000;

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.5);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const rotateIn = keyframes`
  from {
    transform: rotate(-90deg) scale(0);
    opacity: 0;
  }
  to {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
`;

const rotateOut = keyframes`
  from {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
  to {
    transform: rotate(90deg) scale(0);
    opacity: 0;
  }
`;

export const ReportIssueWidget: React.FC<ReportIssueWidgetProps> = ({
  postUrl = "/support",
  options,
}) => {
  const { userInfo } = useContext(BodycamContext);
  const { isChatAvailable, setSidePanelOpen, sidePanelOpen } = useLayout();
  const [userProfile] = userInfo;

  const defaultOptions = useMemo<Option[]>(
    () => [
      {
        value: "request_help_technical_issue",
        label: "Request help / Technical issue",
        icon: <HelpOutlineIcon fontSize="small" color="info" />,
      },
      {
        value: "feedback",
        label: "General feedback",
        icon: <ThumbUpAltOutlinedIcon fontSize="small" color="success" />,
      },
      {
        value: "feature_request",
        label: "Feature request",
        icon: <LightbulbOutlinedIcon fontSize="small" color="warning" />,
      },
      {
        value: "other",
        label: "Other",
        icon: <MoreHorizIcon fontSize="small" color="action" />,
      },
    ],
    []
  );

  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState(defaultOptions[0].value);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  const placeholderByType: Record<string, string> = useMemo(
    () => ({
      request_help_technical_issue:
        "Describe the issue you're experiencing. Include any error messages, what you were trying to do, and steps to reproduce the problem.",
      feedback:
        "Share your thoughts on how we're doing. What's working well? What could be improved?",
      feature_request:
        "Tell us about the feature you'd like to see. How would it help your workflow?",
      other: "Please provide as much detail as possible.",
    }),
    []
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setError(null);
    setSuccess(null);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleClose]);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        type: issueType,
        message,
        url: window.location.href,
        path: window.location.pathname,
        user_agent: navigator.userAgent,
        user_id: userProfile?.id ?? null,
      };
      await rmsClient.post(postUrl, payload);
      setSuccess("Thanks! Your message was submitted successfully.");
      setMessage("");
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (e) {
      console.error(e);
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = () => {
    setOpen((v) => !v);
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setMessage(value);
    }
  };

  const currentOptions = options ?? defaultOptions;
  const selectedOption = currentOptions.find((opt) => opt.value === issueType);

  return (
    <Portal>
      <>
        {/* Floating Action Button */}
        <Box
          sx={{
            position: "fixed",
            bottom: 20,
            right: sidePanelOpen && isChatAvailable && isLgUp ? 420 : 15,
            zIndex: 1001,
            transition: theme.transitions.create("right", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Tooltip
            title={open ? "Close" : "Help & Feedback"}
            placement="left"
            arrow
            enterDelay={300}
          >
            <Fab
              color="primary"
              aria-label={open ? "close feedback panel" : "open feedback panel"}
              onClick={handleToggle}
              size="medium"
              sx={{
                width: 56,
                height: 56,
                border: "2px solid rgba(255, 255, 255, 0.9)",
                boxShadow: open
                  ? "0 6px 20px rgba(25, 118, 210, 0.4)"
                  : "0 4px 14px rgba(0, 0, 0, 0.25)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                animation: !hasInteracted ? `${pulseAnimation} 2s infinite` : "none",
                background: open
                  ? "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"
                  : "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                "&:hover": {
                  transform: "scale(1.08)",
                  boxShadow: "0 8px 25px rgba(25, 118, 210, 0.5)",
                  background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  width: 24,
                  height: 24,
                }}
              >
                <FeedbackIcon
                  sx={{
                    position: "absolute",
                    animation: open
                      ? `${rotateOut} 0.25s ease-out forwards`
                      : `${rotateIn} 0.25s ease-out forwards`,
                    opacity: open ? 0 : 1,
                  }}
                />
                <CloseIcon
                  sx={{
                    position: "absolute",
                    animation: open
                      ? `${rotateIn} 0.25s ease-out forwards`
                      : `${rotateOut} 0.25s ease-out forwards`,
                    opacity: open ? 1 : 0,
                  }}
                />
              </Box>
            </Fab>
          </Tooltip>
        </Box>

        {/* Slide-up Panel */}
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              right: 16,
              bottom: 88,
              zIndex: 1001,
              maxWidth: 420,
              width: "calc(100vw - 32px)",
            }}
          >
            <Paper
              elevation={12}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  color: "white",
                  py: 1.25,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FeedbackIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Help & Feedback
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleClose}
                  aria-label="close"
                  sx={{
                    color: "white",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              {/* Chat Integration */}
              {isChatAvailable && !sidePanelOpen && (
                <Box sx={{ p: 2, pb: 0 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => {
                      setSidePanelOpen(true);
                      handleClose();
                    }}
                    sx={{
                      justifyContent: "flex-start",
                      borderRadius: 2,
                      borderWidth: 1,
                      borderColor: "primary.main",
                      color: "primary.main",
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1,
                      "&:hover": {
                        borderWidth: 1,
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    Ask AI Assistant
                  </Button>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {/* Content */}
              <Box sx={{ p: 2.5, pt: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2.5 }}
                >
                  We'd love to hear from you. Select a category and describe your
                  issue or feedback below.
                </Typography>

                <TextField
                  select
                  label="Category"
                  size="small"
                  fullWidth
                  required
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  sx={{ mb: 2.5 }}
                  slotProps={{
                    select: {
                      renderValue: () => (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {selectedOption?.icon}
                          <span>{selectedOption?.label}</span>
                        </Box>
                      ),
                    },
                  }}
                >
                  {currentOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {opt.icon}
                      </ListItemIcon>
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Message"
                  multiline
                  required
                  rows={7}
                  fullWidth
                  value={message}
                  onChange={handleMessageChange}
                  placeholder={placeholderByType[issueType] || placeholderByType.other}
                  helperText={`${message.length}/${MAX_MESSAGE_LENGTH} characters`}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                    "& .MuiFormHelperText-root": {
                      textAlign: "right",
                      color: message.length > MAX_MESSAGE_LENGTH * 0.9 ? "warning.main" : "text.secondary",
                    },
                  }}
                />

                {/* Status Messages */}
                <Fade in={!!error} timeout={300}>
                  <Box sx={{ mt: 2, display: error ? "block" : "none" }}>
                    <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  </Box>
                </Fade>

                <Fade in={!!success} timeout={300}>
                  <Box sx={{ mt: 2, display: success ? "block" : "none" }}>
                    <Alert
                      severity="success"
                      variant="filled"
                      icon={<CheckCircleOutlineIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      {success}
                    </Alert>
                  </Box>
                </Fade>

                {/* Actions */}
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={1.5}
                  mt={2.5}
                >
                  <Button
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 2.5,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || message.trim().length === 0}
                    endIcon={
                      submitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SendIcon />
                      )
                    }
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 2.5,
                      minWidth: 100,
                    }}
                  >
                    {submitting ? "Sending..." : "Submit"}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Slide>
      </>
    </Portal>
  );
};
